import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import useAxios from "@hooks/use-axios";
import { ONBOARD_VIA, SUPERADMIN_ENDPOINTS } from "../misc/constants";
import { brandDefaults, brandSchema } from "../misc/schema";
import { newBrandExtId } from "../misc/ids";
import { toRows } from "../misc/response";

// Create and list, nothing else — the list is what step 3 reads the api_key and
// ext_id from, so it earns its place; edit and delete do not.
const useSuperadminBrands = () => {
    const { axiosInstance, loading } = useAxios({ base: "base", initialLoader: true, alertTypeOnSuccess: "toast" });
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState("");
    const [createOpen, setCreateOpen] = useState(false);

    const methods = useForm({
        resolver     : yupResolver(brandSchema),
        defaultValues: brandDefaults
    });

    const fetchBrands = async () => {
        try {
            const res = await axiosInstance.get(SUPERADMIN_ENDPOINTS.BRANDS);
            setRows(toRows(res));
        } catch {
            setRows([]);
        }
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    // The list endpoint takes no query params, so the search box filters the
    // rows already on screen rather than pretending to be server-side.
    const filteredRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter((row) => [row.name, row.ext_id, row._id, row.status, row.platform_name]
            .some((field) => String(field || "").toLowerCase().includes(term)));
    }, [rows, search]);

    const openCreate = () => {
        // A fresh id every time the form opens, so two brands made in one
        // sitting never collide on ext_id.
        methods.reset({ ...brandDefaults, ext_id: newBrandExtId() });
        setCreateOpen(true);
    };

    const closeCreate = () => setCreateOpen(false);

    const submit = methods.handleSubmit(async (values) => {
        await axiosInstance.post(SUPERADMIN_ENDPOINTS.BRANDS, {
            ext_id              : values.ext_id,
            name                : values.name,
            status              : values.status,
            payment_model       : values.payment_model,
            debit_note_frequency: values.debit_note_frequency,
            platform_name       : values.platform_name,
            kyc_window          : Number(values.kyc_window),
            notes               : values.notes,
            onboard_via         : ONBOARD_VIA
        });
        closeCreate();
        fetchBrands();
    });

    return {
        rows           : filteredRows,
        total          : rows.length,
        loading,
        search,
        setSearch,
        createOpen,
        methods,
        openCreate,
        closeCreate,
        submit,
        regenerateExtId: () => methods.setValue("ext_id", newBrandExtId(), { shouldValidate: true }),
        refresh        : fetchBrands
    };
};

export default useSuperadminBrands;
